import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  CssBaseline,
  Toolbar,
  useMediaQuery,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/system';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';

// Components
import TopBar from './TopBar';
import SideNav from './SideNav';

// Pages
import CourseGenerator from './pages/CourseGenerator';
import CourseEditor from './pages/CourseEditor';
import SignIn from './pages/SignIn';
import CodeGenerator from './pages/CodeGenerator';
import DiagramGenerator from './pages/DiagramGenerator';
import PresentationGenerator from './pages/PresentationGenerator';
import SelfQuizGenerator from './pages/SelfQuizGenerator';
import InsightGenerator from './pages/InsightGenerator';
import SimulationGenerator from './pages/SimulationGenerator';
import GenerateSpeech from './pages/GenerateSpeech';
import VoiceCloning from './pages/VoiceCloning';
import DisabledFeature from './DisabledFeature';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { CourseProvider } from "./context/CourseContext";
import PublishToClassroom from './pages/PublishToClassroom';
import PublishToLms from './pages/PublishToLms';
import ExamGenerator from './pages/ExamGenerator';
import ReferenceUploads from './pages/ReferenceUploads';
import TeacherApiKeys from './pages/TeacherApiKeys';
import { NetworkStatusProvider } from "./context/NetworkStatusContext";
import { CreditsProvider } from './context/CreditsContext';
import StripeSuccess    from "./pages/StripeSuccess";
import StripeCancelled  from "./pages/StripeCancelled";
import DownloadCourse from './pages/DownloadCourse';

const drawerWidth = 240;

// ---------------------------
//  Auth guard with spinner
// ---------------------------
function RequireAuth({ children }) {
  const { user } = React.useContext(AuthContext);
  const [checking, setChecking] = React.useState(true);

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, () => {
      setChecking(false);
    });
    return unsub;
  }, []);

  if (checking) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return children;
}

function App() {
  // Feature flags
  const isGenerateSpeechEnabled = false;
  const isVoiceCloningEnabled = false;
  const theme = createTheme();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
      <AuthProvider>
        <CourseProvider>
          <NetworkStatusProvider>
            <CreditsProvider>
                <Routes>
                  {/* Full screen SignIn page */}
                  <Route path="/signin" element={<SignIn />} />
                  {/* All other routes will use the main layout with TopBar and Drawer */}
                  <Route
                    path="/*"
                    element={
                      <RequireAuth>
                        <ResponsiveLayout
                          isGenerateSpeechEnabled={isGenerateSpeechEnabled}
                          isVoiceCloningEnabled={isVoiceCloningEnabled}
                        />
                      </RequireAuth>
                    }
                  />
                </Routes>
            </CreditsProvider>
          </NetworkStatusProvider>
        </CourseProvider>
      </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

function ResponsiveLayout({ isGenerateSpeechEnabled, isVoiceCloningEnabled }) {
  const theme = useTheme();
  // "md" breakpoint (900px) is used to determine desktop vs. mobile
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // State to control mobile temporary Drawer
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen((prev) => !prev);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Fixed TopBar on all screen sizes */}
      <TopBar isDesktop={isDesktop} onMobileDrawerToggle={handleMobileDrawerToggle} />

      {/* Desktop: Permanent Drawer; Mobile: Temporary Drawer */}
      {isDesktop ? (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <SideNav />
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true, // Improves mobile performance.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {/* Pass onItemClick so that selecting a menu item closes the Drawer */}
          <SideNav onItemClick={handleMobileDrawerToggle} />
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isDesktop ? `${drawerWidth}px` : 0,
          maxWidth: 2400,   // Set a maximum width for the content
          margin: '0 auto', // Center the content horizontally
        }}
      >
        {/* Toolbar spacer pushes content below fixed TopBar */}
        <Toolbar />
        <Routes>
          <Route path="/generate" element={<CourseGenerator />} />
          <Route path="/edit" element={<CourseEditor />} />
          <Route path="/code-generator" element={<CodeGenerator />} />
          <Route path="/diagram-generator" element={<DiagramGenerator />} />
          <Route path="/presentation-generator" element={<PresentationGenerator />} />
          <Route path="/selfquiz-generator" element={<SelfQuizGenerator />} />
          <Route path="/insight-generator" element={<InsightGenerator />} />
          <Route path="/simulation-generator" element={<SimulationGenerator />} />
          <Route
            path="/generate-speech"
            element={
              isGenerateSpeechEnabled ? (
                <GenerateSpeech />
              ) : (
                <DisabledFeature featureName="Generate Speech" />
              )
            }
          />
          <Route path="/reference-uploads" element={<ReferenceUploads />} />
          <Route path="/teacher-api-keys" element={<TeacherApiKeys />} />
          <Route path="/exam-generator" element={<ExamGenerator />} />
          <Route path="/publish" element={<PublishToClassroom />} />
          <Route path="/me-publish" element={<PublishToLms />} />
          {/* Default route */}
          <Route path="*" element={<CourseGenerator />} />
          <Route path="/stripe-success" element={<StripeSuccess />} />
          <Route path="/stripe-cancelled" element={<StripeCancelled />} />
          <Route path="/download-course" element={<DownloadCourse />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default App;
